import { useState, useEffect, useRef } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Inbox, Navigation, FileText, Coffee, LogOut, HelpCircle, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTacho } from "@/context/TachoContext";
import { Logo } from "@/components/Logo";
import { Onboarding } from "@/components/Onboarding";
import { LanguageSelect } from "@/components/LanguageSelect";
import { t } from "@/i18n";
import { ThemeToggle } from "@/context/ThemeContext";
import { fmtClock } from "@/lib/api";

const TABS = [
  { to: "/", icon: Inbox, label: "Viaggi", id: "viaggi" },
  { to: "/navigatore", icon: Navigation, label: "Navigatore", id: "navigatore" },
  { to: "/documenti", icon: FileText, label: "Documenti", id: "documenti" },
  { to: "/pausa", icon: Coffee, label: "Pausa", id: "pausa" },
];

const BreakBanner = () => {
  const { tacho } = useTacho();
  const nav = useNavigate();
  const loc = useLocation();
  if (!tacho || tacho.level === "ok" || tacho.on_break || loc.pathname === "/pausa") return null;
  return (
    <button data-testid="break-banner" onClick={() => nav("/pausa")} className="nv-press w-full flex items-center gap-3 px-4 py-3 bg-[#ef0298] nv-on-accent text-left">
      <AlertTriangle className="h-7 w-7 shrink-0 nv-blink" />
      <span className="font-display text-lg sm:text-xl font-extrabold uppercase tracking-wide">
        {tacho.level === "exceeded" ? t("Tempo di guida esaurito: fermati ora") : t("Pausa obbligatoria tra {t}", { t: fmtClock(tacho.remaining_sec) })}
        <span className="block text-sm normal-case font-semibold opacity-90">{t("Tocca per andare all'area di servizio più vicina")}</span>
      </span>
    </button>
  );
};

export default function AppShell() {
  const { user, logout } = useAuth();
  const [help, setHelp] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    const el = headerRef.current;
    const ro = new ResizeObserver(() => document.documentElement.style.setProperty("--nv-header", `${el.offsetHeight}px`));
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("nv_onboarded")) {
      setHelp(true);
      localStorage.setItem("nv_onboarded", "1");
    }
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <header ref={headerRef} className="sticky top-0 z-[500] bg-[#0b0f17]/90 backdrop-blur-xl border-b border-[#243047]">
        <div className="flex items-center justify-between px-4 lg:px-8 h-[72px]">
          <Logo size={42} textClassName="hidden sm:inline" />
          <div className="flex items-center gap-2">
            <span className="hidden md:block text-right mr-2" data-testid="driver-name">
              <span className="block text-xs text-slate-400 uppercase tracking-widest">{t("Autista")}</span>
              <span className="font-semibold">{user?.name}</span>
            </span>
            <LanguageSelect />
            <ThemeToggle />
            <button data-testid="help-btn" onClick={() => setHelp(true)} className="nv-press h-14 w-14 grid place-items-center rounded-xl bg-[#161e2e] border border-[#243047]" aria-label={t("Guida")}>
              <HelpCircle className="h-7 w-7 text-[#4accff]" />
            </button>
            <button data-testid="logout-btn" onClick={logout} className="nv-press h-14 w-14 grid place-items-center rounded-xl bg-[#161e2e] border border-[#243047]" aria-label={t("Esci")}>
              <LogOut className="h-7 w-7" />
            </button>
          </div>
        </div>
        <BreakBanner />
      </header>
      <main className="flex-1 pb-28">
        <Outlet />
      </main>
      <nav className="fixed bottom-0 inset-x-0 z-[500] bg-[#0b0f17]/95 backdrop-blur-xl border-t border-[#243047] grid grid-cols-4" data-testid="bottom-nav">
        {TABS.map((tab) => (
          <NavLink key={tab.to} to={tab.to} end={tab.to === "/"} data-testid={`nav-${tab.id}`}
            className={({ isActive }) => `nv-press flex flex-col items-center justify-center gap-1 h-[84px] font-display uppercase font-bold tracking-wide text-base ${isActive ? "text-[#4accff] bg-[#4accff]/10" : "text-slate-300"}`}>
            <tab.icon className="h-8 w-8" strokeWidth={2.2} />
            <span className="text-sm sm:text-base truncate max-w-full px-1">{t(tab.label)}</span>
          </NavLink>
        ))}
      </nav>
      <Onboarding open={help} onClose={() => setHelp(false)} />
    </div>
  );
}
